import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { supabase } from '../../lib/supabase'; // Adjust path
import { useAuth } from '../../contexts/AuthContext'; // Adjust path
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PlayArrowIcon from '@mui/icons-material/PlayArrow'; // Icon for continue
import { format } from 'date-fns'; // For formatting dates

// Interface matching the DB structure + joined profile email
interface AdminRequest {
  id: string;
  requesting_user_id: string;
  request_type: string;
  status: string;
  request_details: any; // JSONB
  created_at: string;
  updated_at: string;
  approving_user_id: string;
  // Joined data
  requesting_user_email?: string;
  approving_user_email?: string;
  organization_id: string;
}

// Define status type based on ENUM (adjust if needed, remember lowercase)
type RequestStatus = 'pending' | 'processing' | 'completed' | 'rejected' | 'cancelled';

export function ManageRequests() {
  const { profile } = useAuth(); // Need profile to confirm superadmin, though route guard does primary check
  const navigate = useNavigate(); // Initialize useNavigate
  const [requests, setRequests] = useState<AdminRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null); // State for success message
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({}); // Track loading per request
  const [selectedStatus, setSelectedStatus] = useState<RequestStatus | 'all'>('pending'); // State for filter

  // Update fetchRequests to accept status filter
  const fetchRequests = useCallback(async (statusFilter: RequestStatus | 'all' = 'pending') => {
    console.log(`Fetching requests with status: ${statusFilter}`);
    setLoading(true);
    setError(null);
    setSuccessMessage(null); // Clear success message on fetch
    try {
      let query = supabase
        .from('admin_requests')
        .select(`
          id,
          requesting_user_id,
          request_type,
          status,
          request_details,
          created_at,
          updated_at,
          approving_user_id,
          profiles:profiles!admin_requests_requesting_user_id_fkey ( email ),
          approver:profiles!admin_requests_approving_user_id_fkey ( email ),
          organization_id
        `)
        .order('created_at', { ascending: false }); // Show newest first generally

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Ensure data is not null before mapping
      let formattedData: AdminRequest[] = [];
      if (data) { 
          // Adjust mapping for optional approver email
          formattedData = data.map(req => ({
            // Cast req to 'any' here to bypass TS error temporarily,
            // assuming structure is correct on success. Add proper typing later if needed.
            ...(req as any), 
            requesting_user_email: (req as any).profiles?.email || 'N/A',
            approving_user_email: (req as any).approver?.email || null,
            organization_id: (req as any).organization_id || '',
          }));
      }

      setRequests(formattedData);

    } catch (err: any) {
      console.error("Error fetching admin requests:", err);
      setError(err.message || 'Failed to load requests.');
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch initial data and re-fetch when filter changes
  useEffect(() => {
    fetchRequests(selectedStatus);
  }, [fetchRequests, selectedStatus]); // Add selectedStatus dependency

  // Handle tab change
  const handleStatusChange = (event: React.SyntheticEvent, newValue: RequestStatus | 'all') => {
    setSuccessMessage(null); // Clear success message on tab change
    setSelectedStatus(newValue);
  };

  const handleAction = useCallback(async (request: AdminRequest, action: 'process' | 'reject') => {
    const requestId = request.id;
    const currentStatus = action === 'process' ? 'processing' : 'rejected';
    const requestingUserId = request.requesting_user_id;

    setActionLoading(prev => ({ ...prev, [requestId]: true }));
    setError(null);
    console.log(`Attempting to set request ${requestId} to ${currentStatus} by user ${profile?.id}`);
    
    if (!profile?.id) {
      setError("User profile not found, cannot perform action.");
      setActionLoading(prev => ({ ...prev, [requestId]: false }));
      return;
    }

    try {
      // Update status in DB
      const { error: updateError } = await supabase
        .from('admin_requests')
        .update({
             status: currentStatus, // Set to 'processing' or 'rejected'
             approving_user_id: profile.id,
             updated_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .eq('status', 'pending'); // Only action pending requests

      if (updateError) throw updateError;

      // --- Insert Notification --- 
      const notificationMessage = action === 'process' 
        ? `Your request (${requestId}) is now being processed.`
        : `Your request (${requestId}) has been rejected.`;
      const notificationType = action === 'process' ? 'request_processing' : 'request_rejected';
      
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
            organization_id: request.organization_id, // Assuming request object has org_id
            user_id: requestingUserId, // Target the user who made the request
            type: notificationType,
            message: notificationMessage,
            related_entity_type: 'admin_request',
            related_entity_id: requestId,
            is_read: false
        });

      if (notificationError) {
          console.error("Failed to create notification for status change:", notificationError);
          // Don't block the UI flow, just log the error
      }
      // --- End Insert Notification --- 

      // If rejecting, just refresh the list
      if (action === 'reject') {
        fetchRequests(selectedStatus);
        setSuccessMessage(`Request ${requestId} has been rejected.`); // Add success message for reject
      } else {
        // If processing (approved), redirect
        navigate(`/admin/process-request/${requestId}`);
      }

    } catch (err: any) {
        console.error(`Error updating request ${requestId} status:`, err);
        setError(err.message || `Failed to ${action === 'process' ? 'start processing' : 'reject'} request.`);
    } finally {
         // Only stop loading indicator if we didn't navigate away
         if (action === 'reject') {
            setActionLoading(prev => ({ ...prev, [requestId]: false }));
         } 
         // If navigating, the component unmounts, no need to set loading false
    }
  }, [fetchRequests, profile?.id, navigate, selectedStatus]);

  // New handler for cancelling processing requests
  const handleCancelProcessing = useCallback(async (request: AdminRequest) => {
    const requestId = request.id;
    const requestingUserId = request.requesting_user_id;

    setActionLoading(prev => ({ ...prev, [requestId]: true }));
    setError(null);
    setSuccessMessage(null); // Clear previous messages
    console.log(`Attempting to cancel processing for request ${requestId} by user ${profile?.id}`);
    
    if (!profile?.id) {
      setError("User profile not found, cannot perform action.");
      setActionLoading(prev => ({ ...prev, [requestId]: false }));
      return;
    }

    try {
      // Update status from 'processing' to 'rejected'
      const { error: updateError } = await supabase
        .from('admin_requests')
        .update({
             status: 'rejected', // Set status to rejected
             approving_user_id: profile.id, // Record user who cancelled
             updated_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .eq('status', 'processing'); // Only cancel processing requests

      if (updateError) throw updateError;

      // --- Insert Notification --- 
       const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
            organization_id: request.organization_id,
            user_id: requestingUserId,
            type: 'request_rejected', // Or maybe 'request_cancelled'?
            message: `Processing for your request (${requestId}) was cancelled (rejected).`,
            related_entity_type: 'admin_request',
            related_entity_id: requestId,
            is_read: false
        });
      if (notificationError) {
          console.error("Failed to create notification for cancellation:", notificationError);
      }
      // --- End Insert Notification --- 

      fetchRequests(selectedStatus);
      setSuccessMessage(`Request ${requestId} processing has been cancelled (status set to rejected).`);

    } catch (err: any) {
        console.error(`Error cancelling processing for request ${requestId}:`, err);
        setError(err.message || `Failed to cancel processing for request.`);
    } finally {
        setActionLoading(prev => ({ ...prev, [requestId]: false }));
    }
  }, [fetchRequests, profile?.id, selectedStatus]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth={false}> {/* Use full width */} 
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Manage Administrative Requests
        </Typography>

        {/* --- Status Filter Tabs --- */} 
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={selectedStatus} onChange={handleStatusChange} aria-label="request status filter">
            <Tab label="Pending" value="pending" />
            <Tab label="Processing" value="processing" />
            <Tab label="Completed" value="completed" />
            <Tab label="Rejected" value="rejected" />
            <Tab label="All" value="all" />
          </Tabs>
        </Box>
        {/* --- End Status Filter Tabs --- */} 

        {/* Display Error and Success Messages */} 
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}

        {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : requests.length === 0 ? (
             <Typography sx={{ mt: 3 }}>No requests found with status '{selectedStatus}'.</Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 750 }} aria-label="admin requests table">
              <TableHead>
                <TableRow>
                  <TableCell>Requested By</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Requested At</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Processed By</TableCell> 
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>{request.requesting_user_email}</TableCell>
                    <TableCell>{request.request_type}</TableCell>
                    <TableCell>
                      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, fontSize: '0.8rem' }}>
                        {JSON.stringify(request.request_details, null, 2)}
                      </pre>
                    </TableCell>
                     <TableCell>
                        <Chip 
                            label={request.status}
                            size="small"
                        />
                    </TableCell>
                    <TableCell>{format(new Date(request.created_at), 'yyyy-MM-dd HH:mm')}</TableCell>
                    <TableCell>{format(new Date(request.updated_at), 'yyyy-MM-dd HH:mm')}</TableCell>
                    <TableCell>{request.approving_user_email || '-'}</TableCell>
                    <TableCell align="center">
                       {/* --- Actions for Pending --- */}
                       {request.status === 'pending' && (
                        actionLoading[request.id] ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <IconButton color="success" onClick={() => handleAction(request, 'process')} aria-label="process request">
                              <CheckCircleIcon />
                            </IconButton>
                            <IconButton color="error" onClick={() => handleAction(request, 'reject')} aria-label="reject request">
                              <CancelIcon />
                            </IconButton>
                          </Box>
                        )
                      )}
                      {/* --- Actions for Processing --- */}
                      {request.status === 'processing' && (
                         actionLoading[request.id] ? (
                            <CircularProgress size={24} />
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <IconButton 
                                  color="primary" 
                                  onClick={() => navigate(`/admin/process-request/${request.id}`)} 
                                  aria-label="continue processing request"
                                  title="Continue Processing"
                               >
                                  <PlayArrowIcon />
                              </IconButton>
                              <IconButton 
                                  color="error" 
                                  onClick={() => handleCancelProcessing(request)} 
                                  aria-label="cancel processing request"
                                  title="Cancel Processing"
                               >
                                  <CancelIcon />
                              </IconButton>
                            </Box>
                          )
                      )}
                      {/* --- No Actions for other statuses --- */}
                      {request.status !== 'pending' && request.status !== 'processing' && (
                         <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Container>
  );
} 